import { ApolloClient, InMemoryCache, createHttpLink, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { onError } from "@apollo/client/link/error";
import { LocalStorageGlobalKeys, ServerResponseErrorCodes } from "constants/constants";

const httpLink = createHttpLink({
    uri: process.env.REACT_APP_HASURA_URL,
});

function getWsHasuraUrl(): string {
    let wsHasuraUrl = process.env.REACT_APP_HASURA_URL_WS; // First read from ENV
    if (!wsHasuraUrl && process.env.REACT_APP_HASURA_URL) {
        // If ws hasura url is not set in env lets use the same as http
        wsHasuraUrl = process.env.REACT_APP_HASURA_URL.replace("https", "wss").replace("http", "ws");
    }
    if (!wsHasuraUrl) {
        throw new Error("Hasura URL not configured. Please add the following ENV variable: REACT_APP_HASURA_URL");
    }
    return wsHasuraUrl;
}

const wsLink = new GraphQLWsLink(
    createClient({
        url: getWsHasuraUrl(),
        connectionParams: () => {
            const token = localStorage.getItem(LocalStorageGlobalKeys.TOKEN);
            return {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "NOTOKEN",
                },
            };
        },
    }),
);

const authLink = setContext((_, { headers }) => {
    const token = localStorage.getItem(LocalStorageGlobalKeys.TOKEN);
    return {
        headers: {
            ...headers,
            ...(token && { Authorization: `Bearer ${token}` }),
        },
    };
});

const logoutLink = onError(({ graphQLErrors = [], networkError = {} as any }) => {
    const status: number = networkError && networkError.statusCode ? networkError.statusCode : null;
    if (
        status === ServerResponseErrorCodes.UNAUTHORIZED ||
        graphQLErrors.some((e: any) => e.extensions.code === "invalid-jwt")
    ) {
        localStorage.removeItem(LocalStorageGlobalKeys.TOKEN);
        window.location.reload();
    }
});

const httpLinkWithAuth = authLink.concat(httpLink);
const httpLinkWithAuthAndLogout = logoutLink.concat(httpLinkWithAuth);

// We want that only subscriptions go through ws
const splitLink = split(
    // split based on operation type
    ({ query }) => {
        const queryDefinition = getMainDefinition(query);
        return queryDefinition.kind === "OperationDefinition" && queryDefinition.operation === "subscription";
    },
    wsLink,
    httpLinkWithAuthAndLogout,
);

export class API {
    client: ApolloClient<object>;
    api_root: string;

    constructor() {
        this.client = new ApolloClient({
            link: splitLink,
            cache: new InMemoryCache(),
        });
        this.api_root = process.env.REACT_APP_API_ROOT as string;
    }
}
